/**
 * Generate Study Dialog - Main Wizard Controller
 *
 * Migrated from: lego/page/etmf/overview/generate-study/Dialog.jsx
 *
 * 7-step wizard for generating new study structure in ETMF
 */

import React, { useState } from 'react'
import Modal from '../../../../components/Modal'
import { Formik, FormikHelpers, FormikProps } from 'formik'
import { DialogState } from './context/DialogState'
import { useDialogContext } from './context/useDialogContext'
import { ValidationSchema } from './Validation'
import Step1 from './Step1'
import Step2 from './Step2'
import Step3 from './Step3'
import Step4 from './Step4'
import Step5 from './Step5'
import Step6 from './Step6'
import Step7 from './Step7'
import LoadingSpinner from '../../../../components/LoadingSpinner'
import { useServiceContext } from '../../../../root'
import { confirmModal } from '../../../../utils/confirmModal'
import type { FormValues } from './context/DialogContext'

interface DialogContentProps {
  onClose: (showConfirmation?: boolean) => void
}

function DialogContent({ onClose }: DialogContentProps) {
  const context = useServiceContext()
  const dialogData = useDialogContext()
  const { translate } = context.service.filter

  const handleSubmit = async (values: FormValues, formikBag: FormikHelpers<FormValues>) => {
    if (dialogData.currentStep === 7) {
      await dialogData.submit()
    } else {
      const currentFieldName = dialogData.getStepName(dialogData.currentStep)
      const nextFieldName = dialogData.getStepName(dialogData.currentStep + 1)

      // Reset touched
      const touchReset = Object.keys(dialogData.getDefaultValues()).reduce(
        (acc, key) => ({ ...acc, [key]: false }),
        {} as Record<string, boolean>
      )
      formikBag.setTouched(touchReset, false)

      // Reset next step if current step was changed
      const currentValues = dialogData.getCurrentValues()
      if (
        nextFieldName &&
        currentFieldName &&
        JSON.stringify(values[currentFieldName as keyof FormValues]) !==
          JSON.stringify(currentValues[currentFieldName as keyof FormValues])
      ) {
        formikBag.setFieldValue(nextFieldName, dialogData.getDefaultValues()[currentFieldName as keyof FormValues])
      }

      dialogData.goToNextStep(values)
    }
  }

  const validate = async (values: FormValues) => {
    try {
      await ValidationSchema(dialogData.currentStep).validate(values, {
        abortEarly: false,
        context: values
      })
      return {}
    } catch (err: any) {
      const errors: Record<string, string> = {}
      if (err.inner) {
        err.inner.forEach((error: any) => {
          if (error.path) {
            // Translate the error message
            errors[error.path] = translate(error.message)
          }
        })
      }
      return errors
    }
  }

  const renderStep = (props: FormikProps<FormValues>) => {
    const stepProps = {
      values: props.values,
      setFieldValue: props.setFieldValue,
      errors: props.errors,
      touched: props.touched
    }

    switch (dialogData.currentStep) {
      case 1:
        return <Step1 />
      case 2:
        return <Step2 {...stepProps} />
      case 3:
        return <Step3 {...stepProps} />
      case 4:
        return <Step4 {...stepProps} />
      case 5:
        return <Step5 {...stepProps} />
      case 6:
        return <Step6 {...stepProps} />
      case 7:
        return <Step7 />
      default:
        return null
    }
  }

  return (
    <>
      <Formik<FormValues>
        validate={validate}
        onSubmit={handleSubmit}
        initialValues={dialogData.getDefaultValues()}
        enableReinitialize
      >
        {(formikProps) => (
          <>
            <Modal.Body>
              {dialogData.isLoading ? <LoadingSpinner centered size={40} /> : renderStep(formikProps)}
            </Modal.Body>
            <Modal.Footer>
              <button type="button" className="btn btn-link" onClick={() => onClose(true)}>
                {translate('CANCEL')}
              </button>
              {dialogData.currentStep > 1 && (
                <button type="button" className="btn btn-secondary" onClick={() => dialogData.goToPreviousStep()}>
                  {translate('ETMF_GENERATE_TRIAL_STRCT_BTN_BACK')}
                </button>
              )}
              <button type="submit" className="btn btn-primary" onClick={() => formikProps.submitForm()}>
                {translate(
                  dialogData.currentStep === 7
                    ? 'ETMF_GENERATE_TRIAL_STRCT_BTN_GENERATE'
                    : 'ETMF_GENERATE_TRIAL_STRCT_BTN_NEXT'
                )}
              </button>
            </Modal.Footer>
          </>
        )}
      </Formik>
    </>
  )
}

interface GenerateStudyDialogProps {
  onClose: () => void
}

export function GenerateStudyDialog({ onClose }: GenerateStudyDialogProps) {
  const context = useServiceContext()
  const { translate } = context.service.filter
  const [showModal, setShowModal] = useState(true)

  const handleClose = (showConfirmation = true) => {
    if (showConfirmation) {
      confirmModal({
        cancelTxt: translate('NO'),
        okTxt: translate('YES'),
        titleMsg: translate('ETMF_GENERATE_TRIAL_STRCT_CANCEL_MODAL_TITLE'),
        msg: <div className="fs12">{translate('ETMF_GENERATE_TRIAL_STRCT_CANCEL_MODAL_TXT')}</div>,
        onSubmit: () => {
          setShowModal(false)
          onClose()
        },
        onCancel: () => {}
      })
    } else {
      setShowModal(false)
      onClose()
    }
  }

  if (!showModal) return null

  return (
    <Modal
      show={showModal}
      handleClose={() => handleClose(true)}
      title={translate('ETMF_GENERATE_TRIAL_STRCT_TITLE')}
      size="md"
    >
      <div className="fs12">
        <DialogState closeModal={handleClose}>
          <DialogContent onClose={handleClose} />
        </DialogState>
      </div>
    </Modal>
  )
}

export default GenerateStudyDialog
